import React, { useState } from 'react';
import { MapPin, Phone, Clock, Calendar, MessageCircle, ClipboardCheck, Stethoscope, GraduationCap, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './LocationBranch.css';
import './LocationCoffsHarbour.css';

const LocationCoffsHarbour = () => {
  const [expandedService, setExpandedService] = useState(null);
  
  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.clinicalminds.net"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Locations",
        "item": "https://www.clinicalminds.net/locations"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Coffs Harbour",
        "item": "https://www.clinicalminds.net/locations/coffs-harbour"
      }
    ]
  };

  const services = [
    {
      icon: <MessageCircle size={32} strokeWidth={1.5} />,
      title: 'Individual and Group Therapy',
      description: 'Personalised one-on-one or group therapy sessions tailored to your unique needs and goals in a safe, supportive environment.'
    },
    {
      icon: <ClipboardCheck size={32} strokeWidth={1.5} />,
      title: 'Psychological Assessments',
      description: 'Comprehensive evaluations to inform treatment planning or for other purposes such as school support, NDIS or other government support.'
    },
    {
      icon: <Stethoscope size={32} strokeWidth={1.5} />,
      title: 'Diagnosis & Treatment Planning',
      description: 'Evidence based diagnostic assessments and comprehensive treatment planning. I work collaboratively with you to understand your needs and develop effective, evidence-based treatment approaches.'
    },
    {
      icon: <GraduationCap size={32} strokeWidth={1.5} />,
      title: 'School Psychology',
      description: 'Dedicated support for learning difficulties, academic challenges, and educational planning. I work with students, families, and schools to create supportive learning environments.'
    },
    {
      icon: <Heart size={32} strokeWidth={1.5} />,
      title: 'NDIS Support',
      description: 'Comprehensive psychological services for NDIS participants. I provide assessments, and capacity building to help you achieve your goals and live independently.'
    }
  ];

  return (
    <div className="page-location location-coffs-harbour">
      <SEO 
        title="Clinical Minds Psychology Coffs Harbour Location | Lachlan Beaton"
        description="Visit Clinical Minds Psychology at 76 Albany Street, Coffs Harbour. Lachlan Beaton - Clinical Psychologist (Registrar). In-person therapy, assessments. Medicare rebates available."
        canonicalUrl="https://www.clinicalminds.net/locations/coffs-harbour"
        schemaData={breadcrumbSchema}
      />
      
      {/* Scroll Indicator for Mobile */}
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Location</span>
          <h1 className="heading-hero">Coffs Harbour</h1>
          <p className="hero-subline">Professional psychology services in the heart of Coffs Harbour</p>
        </div>
      </section>

      <section className="location-details">
        <div className="container">
          <div className="details-grid">
            <div className="detail-main">
              <div className="card detail-card">
                <h2 className="heading-2">Location Information</h2>
                <div className="info-items">
                  <div className="info-item">
                    <MapPin size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Address</h3>
                      <p className="body-medium">
                        <a 
                          href="https://www.google.com/maps/dir//76+Albany+Street,+Coffs+Harbour+NSW+2450" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'var(--blue-primary)', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          76 Albany Street<br />
                          Coffs Harbour, NSW 2450
                        </a>
                      </p>
                      <p className="body-small" style={{ marginTop: '0.5rem' }}>
                        <strong>Parking:</strong> 2 hour metered parking on the street<br />
                        <strong>Entry:</strong> Main entrance to Therapy Collective
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Phone size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Contact</h3>
                      <p className="body-small">
                        Phone: <a href="tel:0266002710">02 6600 2710</a><br />
                        SMS: <a href="sms:0415220944">0415 220 944</a><br />
                        Fax: <a href="tel:0266991457">02 6699 1457</a><br />
                        Email: <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <Clock size={32} strokeWidth={1.5} />
                    <div>
                      <h3 className="heading-3">Availability</h3>
                      <p className="body-medium">
                        <strong>Tuesday:</strong> Assessments<br />
                        <strong>Thursday:</strong> 8am - 5pm<br />
                        <strong>Friday:</strong> 8am - 4pm
                      </p>
                      <p className="body-medium" style={{ marginTop: '0.5rem' }}>
                        <strong>Wednesday and Saturday:</strong> By request
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card services-card">
                <h2 className="heading-2">Services at This Location</h2>
                <p className="body-medium" className="mb-2">
                  At our Coffs Harbour location, we offer comprehensive psychological services tailored to your unique needs.
                </p>
                <div className="services-list">
                  {services.map((service, index) => (
                    <div key={index} className="service-item-expandable">
                      <div 
                        className="service-item clickable"
                        onClick={() => toggleService(index)}
                      >
                        <div className="service-icon-small">{service.icon}</div>
                        <h3 className="heading-3">{service.title}</h3>
                        <div className="expand-icon">
                          {expandedService === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      {expandedService === index && (
                        <div className="service-description">
                          <p className="body-medium">{service.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="detail-sidebar">
              <div className="card cta-card cta-card-enhanced">
                <Calendar size={40} className="cta-icon" />
                <h3 className="heading-2">Request an Appointment</h3>
                <p className="body-medium">
                  Tell us what you need and we'll be in touch within 24 hours.
                </p>
                <div className="cta-benefits">
                  <div className="cta-benefit-item">✓ 24-hour response</div>
                  <div className="cta-benefit-item">✓ Medicare rebates available</div>
                </div>
                <Link to="/bookings" className="btn-primary full-width btn-cta-large">Send a Booking Request</Link>
              </div>
              
              <div className="card map-placeholder">
                <h3 className="heading-3">Find Us</h3>
                <div className="map-box">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3526.4768367942787!2d153.1313!3d-30.29976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b9ddb0a7c5e5e5b%3A0x0!2s76%20Albany%20Street%2C%20Coffs%20Harbour%20NSW%202450!5e0!3m2!1sen!2sau!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0, borderRadius: '8px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Coffs Harbour Location Map"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Support Section */}
      <CrisisSupport />
    </div>
  );
};

export default LocationCoffsHarbour;
